import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createOrder, getSessionOrders, getOrdersByCustomer, verifySessionPayment } from '../services/api';
import { useCart } from '../context/CartContext';
import { useSession } from '../context/SessionContext';
import { useBranding } from '../context/BrandingContext';
import { useLiveAnnouncer } from '../context/LiveAnnouncerContext';
import websocket from '../services/websocket';
import PullToRefresh from '../components/PullToRefresh';
import TakeawayCheckoutModal from '../components/TakeawayCheckoutModal';
import OrderTracker from '../components/OrderTracker';
import BillSummary from '../components/BillSummary/BillSummary';
import BillSplitOptions from '../components/BillSplitOptions/BillSplitOptions';
import LinePaymentModal from '../components/LinePaymentModal';
import MercadoPagoPaymentModal from '../components/MercadoPagoPaymentModal';
import Snackbar from '../components/Snackbar/Snackbar';
import { verifyMercadoPagoPayment } from '../services/api';
import { trackEvent, trackPixel, hashForAnalytics } from '../utils/analytics';
import './OrdersPage.css';

function OrdersPage() {
  const { t } = useTranslation();
  const { cart, clearCart, removeItem, restoreItem, updateQuantity, getSubtotal, getServiceCharge, getTax, getTotal, addItem } = useCart();
  const { session } = useSession();
  const { settings } = useBranding();
  const { announce } = useLiveAnnouncer();
  const [orders, setOrders] = useState([]);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [diners, setDiners] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [totalUnpaid, setTotalUnpaid] = useState(0);
  const [totalSession, setTotalSession] = useState(0);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'bill' ? 'bill' : 'current';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState('');
  const [showTakeawayModal, setShowTakeawayModal] = useState(false);
  const [toast, setToast] = useState('');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [mpStatus, setMpStatus] = useState(null); // 'approved' | 'pending' | 'rejected'
  const [removedItem, setRemovedItem] = useState(null);
  const [showRemoveSnackbar, setShowRemoveSnackbar] = useState(false);
  const removeSnackbarTimer = useRef(null);
  const navigate = useNavigate();

  const sessionId = session?.sessionId;
  const restaurantId = session?.restaurantId;

  useEffect(() => {
    if (!sessionId || !restaurantId) return;

    loadOrders();

    // Conectar a WebSocket para actualizaciones
    websocket.connect(restaurantId, sessionId);
    websocket.on('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
    websocket.on('PAYMENT_RECEIVED', handlePaymentReceived);
    websocket.on('ORDER_UPDATED', handleOrderUpdated);

    return () => {
      websocket.off('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
      websocket.off('PAYMENT_RECEIVED', handlePaymentReceived);
      websocket.off('ORDER_UPDATED', handleOrderUpdated);
      websocket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, restaurantId]);

  const loadOrders = async () => {
    try {
      const response = await getSessionOrders(session.sessionId);
      setOrders(response.orders || []);
      setDiners(response.diners || []);
      setTotalPaid(response.totalPaid || 0);
      setTotalUnpaid(response.totalUnpaid || 0);
      setTotalSession(response.totalSession || 0);
    } catch (err) {
      console.error('Error loading orders:', err);
    }
  };

  // Cargar historial cuando se cambia al tab "history"
  const customerPhone = session?.customer?.phone || session?.customerPhone;
  const loadHistory = useCallback(async () => {
    if (!customerPhone) return;

    setLoadingHistory(true);
    try {
      const response = await getOrdersByCustomer(customerPhone, 30);
      setHistoryOrders(response.orders || []);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [customerPhone]);

  // Pull to refresh handler
  const handleRefresh = useCallback(async () => {
    if (activeTab === 'current' || activeTab === 'bill') {
      await loadOrders();
    } else {
      await loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loadHistory]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  // Handle MercadoPago return URL params
  useEffect(() => {
    const collectionStatus = searchParams.get('collection_status');
    const mpPaymentId = searchParams.get('payment_id');
    const preferenceId = searchParams.get('preference_id');

    if (collectionStatus && (mpPaymentId || preferenceId)) {
      // MP redirected back — map status
      const statusMap = { approved: 'approved', pending: 'pending', rejected: 'rejected', failure: 'rejected' };
      const resolvedStatus = statusMap[collectionStatus] || 'pending';
      setMpStatus(resolvedStatus);
      setPaymentData({ provider: 'mercadopago', amount: null });
      setIsPaymentModalOpen(true);

      // Verify and refresh orders for approved payments
      if (resolvedStatus === 'approved' && mpPaymentId && session?.sessionId) {
        verifyMercadoPagoPayment(mpPaymentId, session.sessionId)
          .then(() => loadOrders())
          .catch((err) => console.error('MP verification error:', err));
      }

      // Clean up URL params
      navigate(window.location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Repetir pedido: agrega los items al carrito
  const handleRepeatOrder = (order) => {
    order.items.forEach(item => {
      addItem({
        itemId: item.itemId,
        name: item.name,
        price: item.price / item.quantity, // Precio unitario
        quantity: item.quantity,
        selectedModifiers: item.selectedModifiers || []
      });
    });
    setToast(t('orders.itemsAdded'));
    setTimeout(() => setToast(''), 2500);

    // Navegar al menú
    if (session?.tableNumber) {
      navigate(`/r/${session.restaurantId}/t/${session.tableNumber}`);
    } else {
      navigate(`/r/${session.restaurantId}/takeaway`);
    }
  };

  const handleOrderStatusChanged = (data) => {
    console.log('Estado de pedido cambiado:', data);
    // Actualizar el pedido en la lista local
    setOrders(prevOrders =>
      prevOrders.map(order =>
        order.orderId === data.data.orderId
          ? { ...order, status: data.data.status, updatedAt: data.data.updatedAt }
          : order
      )
    );
    // Announce status change to screen readers
    const statusKey = `orders.${data.data.status}`;
    announce(t('a11y.orderStatusChanged', {
      orderNumber: data.data.orderNumber || data.data.orderId,
      status: t(statusKey)
    }));
  };

  const handlePaymentReceived = (data) => {
    console.log('Payment received:', data);
    loadOrders();
  };

  const handleOrderUpdated = (data) => {
    console.log('Order updated:', data);
    loadOrders();
  };

  const handlePaymentCreated = (data) => {
    setMpStatus(null);
    setPaymentData(data);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    setIsPaymentModalOpen(false);
    setPaymentData(null);
    trackEvent('payment_completed', {
      restaurant_id: session?.restaurantId,
      session_id: hashForAnalytics(session?.sessionId),
      payment_method: paymentData?.provider || 'unknown',
    });
    trackPixel('Purchase', {
      value: paymentData?.amount || 0,
      currency: 'ARS',
    });
    loadOrders();
    setToast(t('cart.payment_success'));
    setTimeout(() => setToast(''), 2500);
  };

  const handleMpContinue = () => {
    setIsPaymentModalOpen(false);
    setPaymentData(null);
    setMpStatus(null);
    if (mpStatus === 'approved') {
      trackEvent('payment_completed', {
        restaurant_id: session?.restaurantId,
        session_id: hashForAnalytics(session?.sessionId),
        payment_method: 'mercadopago',
      });
      trackPixel('Purchase', {
        value: paymentData?.amount || 0,
        currency: 'ARS',
      });
      setToast(t('cart.payment_success'));
      setTimeout(() => setToast(''), 2500);
    }
    loadOrders();
  };

  const handleVerifyPayment = async ({ paymentId, amount }) => {
    try {
      const result = await verifySessionPayment(paymentId, session.sessionId, amount);
      return result.success;
    } catch (err) {
      console.error('Payment verification failed:', err);
      return false;
    }
  };

  const handleWaiterPayment = (response) => {
    loadOrders();
    setToast(t('bill.paid'));
    setTimeout(() => setToast(''), 2500);
  };

  const handleRemoveItem = useCallback((item) => {
    if (removeSnackbarTimer.current) {
      clearTimeout(removeSnackbarTimer.current);
    }
    const savedItem = { ...item };
    removeItem(item.cartItemId);
    setRemovedItem(savedItem);
    setShowRemoveSnackbar(true);
    announce(t('a11y.itemRemoved', { name: item.name }));
  }, [removeItem, announce, t]);

  const handleUndoRemove = useCallback(() => {
    if (removedItem) {
      restoreItem(removedItem);
      setRemovedItem(null);
      setShowRemoveSnackbar(false);
      announce(t('a11y.itemRestored', { name: removedItem.name }));
    }
  }, [removedItem, restoreItem, announce, t]);

  const handleDismissRemoveSnackbar = useCallback(() => {
    setShowRemoveSnackbar(false);
    setRemovedItem(null);
  }, []);

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;

    // Validar que existe sesión activa
    // Validar que existe sesión activa
    if (!session || !session.restaurantId || !session.sessionId) {
      setError(t('cart.orderError'));
      return;
    }

    // Si es takeaway, mostrar modal
    if (!session.tableNumber) {
      setShowTakeawayModal(true);
      return;
    }

    // Si es dine-in, construir customerInfo desde sesión
    const customerInfo = session.customer?.isGuest === false
      ? {
          name: session.customer.name,
          email: session.customer.email,
          ...(session.customer.phone && { phone: session.customer.phone }),
          dataConsent: true,
        }
      : {
          name: session.customer?.name || 'Guest',
          dataConsent: true,
        };
    await processOrder({ orderType: 'dine_in', tableNumber: session.tableNumber, customerInfo });
  };

  const processOrder = async (additionalData) => {
    setLoading(true);
    setError('');

    try {
      const orderData = {
        restaurantId: session.restaurantId,
        sessionId: session.sessionId,
        ...additionalData,
        items: cart.map(item => ({
          itemId: item.itemId,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          notes: ''
        }))
      };

      await createOrder(orderData);
      trackEvent('order_placed', {
        restaurant_id: session.restaurantId,
        session_id: hashForAnalytics(session.sessionId),
        items_count: cart.length,
        order_total: cart.reduce((sum, i) => sum + i.price * i.quantity, 0),
      });
      clearCart();
      loadOrders();
      setShowTakeawayModal(false);
    } catch (err) {
      setError(t('cart.orderError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTakeawayConfirm = (formData) => {
    const customerInfo = session.customer?.isGuest === false
      ? {
          name: session.customer.name,
          email: session.customer.email,
          phone: formData.phone || session.customer.phone || '',
          notes: formData.notes,
          dataConsent: true,
        }
      : formData;
    processOrder({
      orderType: 'takeaway',
      customerInfo
    });
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: t('orders.pending'),
      preparing: t('orders.preparing'),
      ready: t('orders.ready'),
      delivered: t('orders.delivered')
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      pending: '#ff9800',
      preparing: '#2196F3',
      ready: '#A0F487',
      delivered: '#9E9E9E'
    };
    return colorMap[status] || '#666';
  };

  // Mostrar mensaje si no hay sesión activa
  if (!session) {
    return (
      <div className="orders-page">
        <div className="orders-header">
          <button onClick={() => navigate('/')} className="back-btn">←</button>
          <h1>{t('orders.title')}</h1>
          <div></div>
        </div>
        <div className="container">
          <div className="card">
            <p>{t('orders.noOrders')}</p>
            <button className="btn btn-primary" onClick={() => navigate('/')}>
              {t('scan.scanButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-header">
        <button onClick={() => navigate(-1)} className="back-btn">←</button>
        <h1>{t('orders.title')}</h1>
        <div></div>
      </div>

      {/* Toast notification */}
      {toast && <div className="orders-toast" role="status" aria-live="polite">{toast}</div>}

      <PullToRefresh onRefresh={handleRefresh}>
      {/* Tabs */}
      <div className="orders-tabs">
        <button
          className={`orders-tab ${activeTab === 'current' ? 'active' : ''}`}
          onClick={() => setActiveTab('current')}
        >
          {t('orders.currentVisit')}
        </button>
        <button
          className={`orders-tab ${activeTab === 'bill' ? 'active' : ''}`}
          onClick={() => setActiveTab('bill')}
        >
          {t('orders.bill')}
        </button>
        <button
          className={`orders-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          {t('orders.history')}
        </button>
      </div>

      <div className="container">
        {/* Carrito Actual - solo en tab current */}
        {activeTab === 'current' && cart.length > 0 && (
          <div className="cart-section card">
            <h2>{t('cart.title')}</h2>
            {cart.map((item) => (
              <div key={item.cartItemId} className="cart-item">
                <div className="cart-item-info">
                  <h3>{item.name}</h3>
                  <p className="cart-item-price">${item.price} x {item.quantity}</p>
                </div>
                <div className="cart-item-actions">
                  <button onClick={() => item.quantity === 1
                    ? handleRemoveItem(item)
                    : updateQuantity(item.cartItemId, item.quantity - 1)}>
                    <span className="material-icons" aria-hidden="true">{item.quantity === 1 ? 'delete' : 'remove'}</span>
                  </button>
                  <span>{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.cartItemId, item.quantity + 1)}>
                    <span className="material-icons" aria-hidden="true">add</span>
                  </button>
                </div>
              </div>
            ))}

            <div className="cart-total">
              <div className="total-breakdown">
                <div className="total-line">
                  <span>{t('cart.subtotal')}:</span>
                  <span>${getSubtotal().toFixed(2)}</span>
                </div>
                <div className="total-line">
                  <span>{t('cart.serviceCharge')} ({settings?.serviceCharge ?? 10}%):</span>
                  <span>${getServiceCharge(settings).toFixed(2)}</span>
                </div>
                <div className="total-line">
                  <span>{t('cart.tax')} ({settings?.taxRate ?? 21}%):</span>
                  <span>${getTax(settings).toFixed(2)}</span>
                </div>
                <div className="total-line total-final">
                  <strong>{t('cart.total')}:</strong>
                  <strong>${getTotal(settings).toFixed(2)}</strong>
                </div>
              </div>
            </div>

            {error && <div className="error">{error}</div>}

            <button
              className="btn btn-primary"
              onClick={handlePlaceOrder}
              disabled={loading}
            >
              {loading ? t('common.loading') : t('cart.placeOrder')}
            </button>
          </div>
        )}

        {/* Tab: Pedidos de esta visita */}
        {activeTab === 'current' && (
          <div className="orders-section">
            <h2>{t('orders.currentOrder')}</h2>
            {orders.length === 0 ? (
              <p className="no-orders">{t('orders.noOrders')}</p>
            ) : (
              orders.map((order) => (
                <div key={order.orderId} className="order-card card">
                  <div className="order-header">
                    <span className="order-id">{t('orders.orderNumber')}{order.orderId.slice(-6)}</span>
                    {order.status === 'delivered' && (
                      <span
                        className="order-status"
                        style={{ backgroundColor: getStatusColor(order.status) }}
                      >
                        {getStatusText(order.status)}
                      </span>
                    )}
                  </div>

                  {/* Visual tracker for active orders */}
                  {order.status !== 'delivered' && (
                    <OrderTracker status={order.status} />
                  )}

                  {order.queuePosition && ['pending', 'preparing'].includes(order.status) && (
                    <div className="queue-position">
                      <p>{t('orders.queuePosition', { position: order.queuePosition })}</p>
                      <small>{t('orders.queueInfo', { position: order.queuePosition })}</small>
                    </div>
                  )}

                  <div className="order-items">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="order-item">
                        <span>{item.quantity}x {item.name}</span>
                        <span>${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="order-total">
                    {order.subtotal && (
                      <div className="total-breakdown">
                        <div className="total-line">
                          <span>{t('orders.subtotal')}:</span>
                          <span>${order.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="total-line">
                          <span>{t('orders.serviceCharge')} (10%):</span>
                          <span>${order.serviceCharge.toFixed(2)}</span>
                        </div>
                        <div className="total-line">
                          <span>{t('orders.tax')} (21%):</span>
                          <span>${order.tax.toFixed(2)}</span>
                        </div>
                        <div className="total-line total-final">
                          <strong>{t('orders.total')}:</strong>
                          <strong>${order.total.toFixed(2)}</strong>
                        </div>
                      </div>
                    )}
                    {!order.subtotal && (
                      <strong>{t('orders.total')}: ${order.total.toFixed(2)}</strong>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Tab: Cuenta de la mesa */}
        {activeTab === 'bill' && (
          <div className="orders-section">
            {orders.length === 0 ? (
              <p className="no-orders">{t('orders.noOrders')}</p>
            ) : (
              <BillSummary
                orders={orders}
                diners={diners}
                totalSession={totalSession}
                totalPaid={totalPaid}
                totalUnpaid={totalUnpaid}
                settings={settings}
                tableNumber={session?.tableNumber}
              >
                {totalUnpaid > 0 && (
                  <BillSplitOptions
                    totalUnpaid={totalUnpaid}
                    diners={diners}
                    onPaymentCreated={handlePaymentCreated}
                    onWaiterPayment={handleWaiterPayment}
                  />
                )}
              </BillSummary>
            )}
          </div>
        )}

        {/* Tab: Historial completo */}
        {activeTab === 'history' && (
          <div className="orders-section">
            <h2>{t('orders.orderHistory')}</h2>
            {loadingHistory ? (
              <div className="loading-spinner">{t('common.loading')}</div>
            ) : historyOrders.length === 0 ? (
              <p className="no-orders">{t('orders.noHistory')}</p>
            ) : (
              historyOrders.map((order) => (
                <div key={order.orderId} className="order-card card">
                  <div className="order-header">
                    <div className="order-header-info">
                      <span className="order-id">{t('orders.orderNumber')}{order.orderId.slice(-6)}</span>
                      <span className="order-date">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <span
                      className="order-status"
                      style={{ backgroundColor: getStatusColor(order.status) }}
                    >
                      {getStatusText(order.status)}
                    </span>
                  </div>

                  <div className="order-items">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="order-item">
                        <span>{item.quantity}x {item.name}</span>
                        <span>${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="order-total">
                    <div className="total-line total-final">
                      <strong>{t('orders.total')}:</strong>
                      <strong>${order.total.toFixed(2)}</strong>
                    </div>

                    {/* Botón Repetir pedido */}
                    <button
                      className="order-repeat-btn"
                      onClick={() => handleRepeatOrder(order)}
                    >
                      <span className="material-icons" aria-hidden="true">replay</span>
                      {t('orders.repeatOrder')}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      </PullToRefresh>

      <TakeawayCheckoutModal
        isOpen={showTakeawayModal}
        onClose={() => setShowTakeawayModal(false)}
        onConfirm={handleTakeawayConfirm}
        loading={loading}
      />

      {/* Line Payment Modal for session payments */}
      {isPaymentModalOpen && paymentData?.provider !== 'mercadopago' && (
        <LinePaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(false);
            setPaymentData(null);
          }}
          paymentId={paymentData?.paymentId}
          amount={paymentData?.amount}
          orderId={null}
          qr={paymentData?.qr}
          deeplink={paymentData?.deeplink}
          onSuccess={handlePaymentSuccess}
          onVerifyPayment={handleVerifyPayment}
        />
      )}

      {/* MercadoPago Modal for MP Checkout Pro */}
      {isPaymentModalOpen && paymentData?.provider === 'mercadopago' && (
        <MercadoPagoPaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(false);
            setPaymentData(null);
            setMpStatus(null);
          }}
          checkoutUrl={paymentData?.checkoutUrl}
          amount={paymentData?.amount}
          status={mpStatus}
          onContinue={handleMpContinue}
        />
      )}

      <Snackbar
        message={t('cart.itemRemoved', { name: removedItem?.name })}
        isVisible={showRemoveSnackbar}
        onDismiss={handleDismissRemoveSnackbar}
        onUndo={handleUndoRemove}
        duration={5000}
      />
    </div>
  );
}

export default OrdersPage;
